import { StateCreator } from 'zustand';
import { CedarStore } from './types';
import { v4 as uuidv4 } from 'uuid';
import {
	updateOnboardingLog,
	createOnboardingLog,
} from '@/components/onboarding/onboardingSupabase';
import React from 'react';
import { WaveText } from '@/components/onboarding/WaveText';
import { EmojiData } from '@/components/onboarding/components/EmojiPicker';
import { useOnboarding, setCedarStore } from './CedarStore';
import { getThemeFromBackground } from '@/styles/stylingUtils';
import { DEFAULT_COLOR } from '@/store/stylingSlice';

// Base step that all onboarding steps inherit from
export interface BaseOnboardingStep<
	T =
		| string
		| string[]
		| { emoji: string; name: string }
		| { [key: string]: string },
> {
	id?: string;
	title: string;
	description?: string;
	value?: T;
	isRequired?: boolean;
	isCompleted?: boolean;
	progressBonus?: number;
	bonusApplied?: boolean;
	conditionalSteps?: { [key: string]: OnboardingStep };
	actionText?: {
		text: string;
		actionText: string;
		onClick: (onboarding: ReturnType<typeof useOnboarding>) => void;
	}[];
	buttonText?: string;
	onEnd?: (onboarding: ReturnType<typeof useOnboarding>) => void;
	secondaryButtons?: {
		text: string;
		variant?: 'primary' | 'secondary';
		onClick: (onboarding: ReturnType<typeof useOnboarding>) => void;
	}[];
}

// Export the activateOnboardingSteps type
export type ActivateOnboardingSteps = (
	steps: OnboardingStep[],
	onboardingConfigId?: string
) => Promise<void>;

// Multiple choice step with options
export interface MultipleChoiceStep extends BaseOnboardingStep<string[]> {
	type: 'multiple_choice';
	options: (
		| string
		| {
				label: string;
				description?: string;
				icon?: string;
				subOptions?: (
					| string
					| {
							label: string;
							description?: string;
							icon?: string;
					  }
				)[];
		  }
	)[];
	value?: string[];
	allowMultipleSelections?: boolean;
	grid?: boolean;
}

// Text input step
export interface InputStep extends BaseOnboardingStep<string> {
	type: 'input';
	placeholder?: string;
	label?: string;
	validation?: (value: string) => boolean;
}

// Base interface for common input properties
interface BaseInputItem {
	id: string;
	label: string;
	placeholder?: string;
	required?: boolean;
}

// Freeform input type
export interface FreeformInput extends BaseInputItem {
	type: 'freeform';
	validation?: (value: string) => boolean;
}

// Selection input type with options
export interface SelectionInput extends BaseInputItem {
	type: 'selection';
	options: string[];
}

// Rating input type
export interface RatingInput extends BaseInputItem {
	type: 'rating';
	min: number;
	max: number;
	labels?: {
		min: string;
		max: string;
	};
}

// Long text input type
export interface LongTextInput extends BaseInputItem {
	type: 'longText';
}

// Union type for all input types
export type MultiInputItem =
	| FreeformInput
	| SelectionInput
	| RatingInput
	| LongTextInput;

// Multi-input step that handles multiple inputs of different types
export interface MultiInputStep
	extends BaseOnboardingStep<{ [key: string]: string }> {
	type: 'multi_input';
	inputs: MultiInputItem[];
}

// Color picker step
export interface ColorStep extends BaseOnboardingStep<string> {
	type: 'color';
	selectorType?: 'wheel' | string[];
}

// Description step with icon and button
export interface DescriptionStep extends BaseOnboardingStep<string> {
	type: 'description';
	icon?: string;
	buttonText?: string;
}

// Login step for authentication
export interface LoginStep extends BaseOnboardingStep<string> {
	type: 'login';
	email?: {
		onSubmit: (
			email: string,
			password: string,
			onboarding: ReturnType<typeof useOnboarding>
		) => Promise<string | false | { errorMessage: string }>;
		validation?: {
			validationMethod: (email: string) => boolean;
			errorMessage: string;
		}[];
		placeholder?: string;
		prefill?: string;
		emailOnly?: boolean;
	};
	google?: {
		onSubmit: (
			onboarding: ReturnType<typeof useOnboarding>
		) => Promise<string | false | { errorMessage: string }>;
	};
	github?: {
		onSubmit: (
			onboarding: ReturnType<typeof useOnboarding>
		) => Promise<string | false | { errorMessage: string }>;
	};
	okta?: {
		onSubmit: (
			onboarding: ReturnType<typeof useOnboarding>
		) => Promise<string | false | { errorMessage: string }>;
	};
	microsoft?: {
		onSubmit: (
			onboarding: ReturnType<typeof useOnboarding>
		) => Promise<string | false | { errorMessage: string }>;
	};
	autoSignin?: boolean;
	enrich?: boolean;
	errorMessage?: string;
}

// Add the WorkspaceNameStep interface
export interface WorkspaceNameStep
	extends BaseOnboardingStep<{ emoji: string; name: string }> {
	type: 'workspace_input';
	imageChoices?: EmojiData[];
}

// Credit step for showing credit animations
export interface CreditStep extends BaseOnboardingStep<string> {
	type: 'credit';
	additionalCredits?: number;
}

// Union type for all onboarding step types
export type OnboardingStep =
	| MultipleChoiceStep
	| InputStep
	| ColorStep
	| DescriptionStep
	| LoginStep
	| WorkspaceNameStep
	| MultiInputStep
	| CreditStep;

// Interface for the OnboardingSlice state and actions
export interface OnboardingSlice {
	// State
	onboardingSteps: OnboardingStep[];
	stepNum: number;
	isOnboardingActive: boolean;
	isOnboardingComplete: boolean;
	progressBar: {
		totalValue: number;
		currentValue: number | null;
		label?: string;
		showCredits?: boolean;
	};
	onboardingLogId: string;

	// Actions
	setOnboardingSteps: (steps: OnboardingStep[]) => void;
	addOnboardingStep: (step: OnboardingStep) => void;
	addOnboardingSteps: (steps: OnboardingStep[]) => void;
	upsertOnboardingSteps: (steps: OnboardingStep[]) => void;
	removeOnboardingStep: (id: string) => void;
	clearOnboardingSteps: () => void;
	nextStep: () => void;
	prevStep: () => void;
	goToStep: (stepNum: number) => void;
	setOnboardingStepValue: (
		value: string | string[] | { [key: string]: string },
		field?: 'emoji' | 'name'
	) => void;
	setIsOnboardingActive: (active: boolean, onboardingConfigId?: string) => void;
	completeOnboarding: () => void;
	resetOnboarding: () => void;
	setProgressBarValue: (currentValue: number | null) => void;
	setProgressBarTotal: (totalValue: number) => void;
	setProgressBar: (updates: Partial<OnboardingSlice['progressBar']>) => void;
	applyStepBonus: (stepId?: string) => void;
	setOnboardingLogId: (onboardingLogId: string) => void;
	getStepValue: (stepOrId: OnboardingStep | string) => string | React.ReactNode;
	renderStepText: (text: string) => React.ReactNode;
	activateOnboardingSteps: (
		steps: OnboardingStep[],
		onboardingConfigId?: string
	) => Promise<void>;
}

// Create the onboarding slice
export const createOnboardingSlice: StateCreator<
	CedarStore,
	[],
	[],
	OnboardingSlice
> = (set, get) => ({
	// Default state
	onboardingSteps: [],
	stepNum: 0,
	isOnboardingActive: false,
	isOnboardingComplete: false,
	progressBar: {
		totalValue: 1000,
		currentValue: null,
		label: 'credits',
		showCredits: true,
	},
	onboardingConfigId: '',
	onboardingLogId: '',
	// Actions
	setOnboardingSteps: (steps: OnboardingStep[]) => {
		// Generate IDs for steps that don't have them
		const stepsWithIds = steps.map((step) => ({
			...step,
			id: step.id || uuidv4(),
		}));

		set({
			onboardingSteps: stepsWithIds,
			stepNum: 0,
			isOnboardingComplete: false,
			progressBar: {
				...get().progressBar,
			},
		});
	},

	addOnboardingStep: (step: OnboardingStep) => {
		const stepWithId = {
			...step,
			id: step.id || uuidv4(),
		} as OnboardingStep;

		set((state) => ({
			onboardingSteps: [...state.onboardingSteps, stepWithId],
		}));
	},

	addOnboardingSteps: (steps: OnboardingStep[]) => {
		const stepsWithIds = steps.map((step) => ({
			...step,
			id: step.id || uuidv4(),
		})) as OnboardingStep[];

		set((state) => ({
			onboardingSteps: [...state.onboardingSteps, ...stepsWithIds],
		}));
	},

	removeOnboardingStep: (id: string) => {
		set((state) => ({
			onboardingSteps: state.onboardingSteps.filter((step) => step.id !== id),
		}));
	},

	upsertOnboardingSteps: (steps: OnboardingStep[]) => {
		set((state) => {
			// Generate IDs only for steps that don't have them
			const stepsWithIds = steps.map((step) => ({
				...step,
				id: step.id || uuidv4(),
			})) as OnboardingStep[];

			// Keep all steps before and including the current step
			const stepsBeforeCurrent = state.onboardingSteps.slice(
				0,
				state.stepNum + 1
			);

			// Keep all future steps
			const futureSteps = state.onboardingSteps.slice(state.stepNum + 1);

			// Combine the steps before current, new steps, and future steps
			return {
				onboardingSteps: [
					...stepsBeforeCurrent,
					...stepsWithIds,
					...futureSteps,
				],
				isOnboardingComplete: false,
			};
		});
	},

	clearOnboardingSteps: () => {
		set({
			onboardingSteps: [],
			stepNum: 0,
			isOnboardingComplete: false,
		});
	},

	nextStep: () => {
		set((state) => {
			const nextStepNum = state.stepNum + 1;
			const isLastStep = nextStepNum >= state.onboardingSteps.length;

			// Call onEnd callback of current step if it exists
			const currentStep = state.onboardingSteps[state.stepNum];
			if (currentStep?.onEnd) {
				currentStep.onEnd(get());
			}

			// Trigger enrichment if this is a Login step with enrich flag and enterprise email
			const store = get();

			if (
				currentStep?.type === 'login' &&
				(currentStep as LoginStep).enrich &&
				typeof currentStep.value === 'string' &&
				isEnterpriseEmail(currentStep.value) &&
				store.userId &&
				store.productId
			) {
				const domain = currentStep.value.split('@')[1].toLowerCase();
				runUserEnrichment(
					currentStep.value,
					domain,
					store.userId as string,
					store.productId as string
				);
			}

			// If this is the last step, mark onboarding as complete
			if (isLastStep) {
				get().completeOnboarding();
				return {};
			}

			// Save progress after each step
			if (get().onboardingLogId) {
				try {
					updateOnboardingLog(get().onboardingLogId, {
						onboardingSteps: get().onboardingSteps,
						completed: false,
					});
				} catch (error) {
					console.error('Error updating onboarding log:', error);
				}
			}

			// Return only the updated step number
			return { stepNum: nextStepNum };
		});
	},

	prevStep: () => {
		set((state) => ({
			stepNum: Math.max(0, state.stepNum - 1),
		}));
	},

	goToStep: (stepNum: number) => {
		set((state) => ({
			stepNum: Math.min(Math.max(0, stepNum), state.onboardingSteps.length - 1),
		}));
	},

	setOnboardingStepValue: (
		value: string | string[] | { [key: string]: string },
		field?: 'emoji' | 'name'
	) => {
		// If current step is a login step, persist the email value
		const stepNum = get().stepNum;
		const currentStep = get().onboardingSteps[stepNum];
		if (
			currentStep?.type === 'login' &&
			typeof value === 'string' &&
			/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
		) {
			get()
				.addEmailToProductUser(value)
				.catch((err: unknown) =>
					console.error('Error adding email to product_user:', err)
				);
		}
		set((state) => {
			if (
				state.onboardingSteps.length === 0 ||
				state.stepNum >= state.onboardingSteps.length
			) {
				return state;
			}

			const updatedSteps = [...state.onboardingSteps];
			const currentStep = updatedSteps[state.stepNum];

			if (currentStep.type === 'multiple_choice') {
				const valueArray = Array.isArray(value)
					? value
					: typeof value === 'string'
						? [value]
						: [];
				updatedSteps[state.stepNum] = {
					...currentStep,
					value: valueArray,
					isCompleted: valueArray.length > 0,
				} as MultipleChoiceStep;
			} else if (currentStep.type === 'multi_input') {
				// Handle multi_input step
				updatedSteps[state.stepNum] = {
					...currentStep,
					value: value as { [key: string]: string },
					isCompleted: true,
				};
			} else if (currentStep.type === 'workspace_input') {
				const currentValue = currentStep.value || { emoji: '', name: '' };
				updatedSteps[state.stepNum] = {
					...currentStep,
					value: {
						...currentValue,
						[field || 'name']: value,
					},
					isCompleted: true,
				} as WorkspaceNameStep;
			} else {
				// Handle all other step types
				updatedSteps[state.stepNum] = {
					...currentStep,
					value,
					isCompleted: true,
				} as OnboardingStep;
			}

			// Handle conditional steps

			// Helper function to stringify step values
			const stringifyStepValue = (step: OnboardingStep): string => {
				if (step.type === 'multiple_choice' && Array.isArray(step.value)) {
					// For conditional steps, only use the first item (parent label) in the array
					return step.value[1] || step.value[0] || '';
				}
				if (step.type === 'workspace_input' && step.value) {
					return `${step.value.emoji}-${step.value.name}`;
				}
				if (step.type === 'multi_input' && typeof step.value === 'object') {
					return JSON.stringify(step.value);
				}
				return String(step.value || '');
			};

			const stringifiedValue = stringifyStepValue(updatedSteps[state.stepNum]);

			// Get all possible conditional step IDs from the current step
			const conditionalStepIds = currentStep.conditionalSteps
				? Object.values(currentStep.conditionalSteps)
						.map((step) => step.id)
						.filter(Boolean)
				: [];

			// Remove any existing conditional steps that match the IDs
			const filteredSteps = updatedSteps.filter((step) => {
				// Keep the step if it's not one of the conditional step IDs
				return !conditionalStepIds.includes(step.id);
			});

			// Check if conditional steps exist for the current value
			let conditionalStepExists = false;

			// Then add the new conditional step if it exists
			if (
				currentStep.conditionalSteps &&
				currentStep.conditionalSteps[stringifiedValue]
			) {
				const conditionalStep = currentStep.conditionalSteps[stringifiedValue];
				// Insert the conditional step after the current step
				filteredSteps.splice(state.stepNum + 1, 0, {
					...conditionalStep,
					id: conditionalStep.id || `conditional-${currentStep.id}}`,
				});
				conditionalStepExists = true;
			}

			// If the conditionalSteps exists but no specific match was found for the current value,
			// and there's an "Other" option, add that as a fallback
			if (
				!conditionalStepExists &&
				currentStep.conditionalSteps &&
				currentStep.conditionalSteps['Other']
			) {
				const otherStep = currentStep.conditionalSteps['Other'];
				// Insert the "Other" fallback step after the current step
				filteredSteps.splice(state.stepNum + 1, 0, {
					...otherStep,
					id: otherStep.id || `conditional-${currentStep.id}`,
				});
			}

			return { onboardingSteps: filteredSteps };
		});
	},

	setIsOnboardingActive: async (
		active: boolean,
		onboardingConfigId?: string
	) => {
		const userId = get().userId;
		const productId = get().productId;

		if (!get().isOnboardingActive && active && userId && productId) {
			try {
				const data = await createOnboardingLog(
					userId,
					productId,
					onboardingConfigId
				);
				set({ onboardingLogId: data.log?.id ?? '' });
			} catch (error) {
				console.error('Error creating onboarding log:', error);
			}
		}

		set({ isOnboardingActive: active });
	},

	completeOnboarding: () => {
		set({
			isOnboardingComplete: true,
			isOnboardingActive: false,
			progressBar: {
				...get().progressBar,
				currentValue: null,
			},
		});

		if (get().onboardingLogId) {
			try {
				updateOnboardingLog(get().onboardingLogId, {
					onboardingSteps: get().onboardingSteps,
					completed: true,
				});
			} catch (error) {
				console.error('Error completing onboarding log:', error);
			}
		}
	},

	resetOnboarding: () => {
		if (get().onboardingLogId) {
			try {
				updateOnboardingLog(get().onboardingLogId, {
					onboardingSteps: get().onboardingSteps,
					completed: true,
				});
			} catch (error) {
				console.error('Error completing onboarding log:', error);
			}
		}

		set({
			stepNum: 0,
			isOnboardingComplete: false,
			progressBar: {
				...get().progressBar,
				currentValue: 0,
			},
			onboardingSteps: get().onboardingSteps.map((step) => {
				const resetStep = {
					...step,
					isCompleted: false,
				};

				// Handle value reset based on step type
				switch (step.type) {
					case 'multiple_choice':
						return {
							...resetStep,
							value: [] as string[],
						} as MultipleChoiceStep;
					case 'input':
						return {
							...resetStep,
							value: undefined,
						} as InputStep;
					case 'color':
						return {
							...resetStep,
							value: undefined,
						} as ColorStep;
					case 'description':
						return {
							...resetStep,
							value: undefined,
						} as DescriptionStep;
					case 'login':
						return {
							...resetStep,
							value: undefined,
						} as LoginStep;
					case 'workspace_input':
						return {
							...resetStep,
							value: { emoji: '', name: '' },
						} as WorkspaceNameStep;
					default:
						return resetStep as OnboardingStep;
				}
			}),
		});
	},

	setProgressBar: (updates: Partial<OnboardingSlice['progressBar']>) => {
		set((state) => ({
			progressBar: {
				...state.progressBar,
				...updates,
			},
		}));
	},

	setProgressBarValue: (currentValue: number | null) => {
		set((state) => ({
			progressBar: {
				...state.progressBar,
				currentValue: currentValue,
			},
		}));
	},

	setProgressBarTotal: (totalValue: number) => {
		set((state) => ({
			progressBar: {
				...state.progressBar,
				totalValue,
			},
		}));
	},

	applyStepBonus: (stepId?: string) => {
		set((state) => {
			let currentStep: OnboardingStep | undefined;

			// If stepId is provided, find that specific step
			if (stepId) {
				currentStep = state.onboardingSteps.find((step) => step.id === stepId);
			}
			// Otherwise use the current step based on stepNum
			else if (state.stepNum < state.onboardingSteps.length) {
				currentStep = state.onboardingSteps[state.stepNum];
			}

			// If no valid step found, return unchanged state
			if (!currentStep || currentStep.bonusApplied) {
				return state;
			}

			// Calculate new progress value
			const bonusAmount = currentStep.progressBonus || 0;
			const newProgressValue = Math.min(
				state.progressBar.totalValue,
				(state.progressBar.currentValue || 0) + bonusAmount
			);

			// Update both the step and the progress bar
			const updatedSteps = state.onboardingSteps.map((step) =>
				step.id === currentStep?.id ? { ...step, bonusApplied: true } : step
			);

			return {
				onboardingSteps: updatedSteps,
				progressBar: {
					...state.progressBar,
					currentValue: newProgressValue,
				},
			};
		});
	},

	setOnboardingLogId: (onboardingLogId: string) => {
		set({ onboardingLogId });
	},
	getStepValue: (
		stepOrId: OnboardingStep | string
	): string | React.ReactNode => {
		// If we received a string (stepId), we need to get the step from the store
		if (typeof stepOrId === 'string') {
			// First check if it's a variable
			const variable = get().variables[stepOrId];
			if (variable !== undefined) {
				return variable;
			}

			// Then check if it's a step
			const step = get().onboardingSteps.find((s) => s.id === stepOrId);
			if (!step) return '';
			return get().getStepValue(step);
		}

		const step = stepOrId;

		// Handle workspace name step
		if (step.type === 'workspace_input') {
			return step.value?.name || '';
		}

		// Handle multiple choice step
		if (
			step.type === 'multiple_choice' &&
			Array.isArray(step.value) &&
			step.value.length > 1
		) {
			return `${step.value[0]} +${step.value.length - 1}`;
		}

		// Handle other step types
		if (Array.isArray(step.value)) {
			return step.value[0] || '';
		}

		return (step.value as string) || '';
	},

	renderStepText: (text: string): React.ReactNode => {
		// Split the text by ${...} patterns while preserving whitespace
		const parts = text.split(/(\s*)(\${[^}]+})(\s*)/g).filter(Boolean);

		return React.createElement(
			'div',
			{
				className:
					'flex flex-wrap gap-0.5 items-center justify-center text-center',
			},
			parts.map((part, index) => {
				// Check if this part is a variable reference (matches ${...})
				const match = part.match(/\${([^}]+)}/);
				if (match) {
					// First check if the part contains "-theme" and replace it based on background color
					if (match[1].includes('-theme')) {
						const color = get().styling.color || DEFAULT_COLOR;
						// Reverse because dark needs light logo and vice versa.
						const theme =
							getThemeFromBackground(color) === 'light' ? 'dark' : 'light';
						match[1] = match[1].replace('-theme', `-${theme}`);
					}

					if (match[1].startsWith('wave-')) {
						const waveText = match[1].replace('wave-', '');
						return React.createElement(WaveText, {
							key: index,
							text: waveText,
						});
					}

					if (match[1].startsWith('bold-')) {
						const boldText = match[1].replace('bold-', '');
						return React.createElement(
							'span',
							{
								key: index,
								className: 'font-semibold whitespace-nowrap',
							},
							boldText
						);
					}

					const variableKey = match[1];
					const value = get().getStepValue(variableKey);
					return React.createElement('span', { key: index }, value);
				}

				// For regular text, check if it's the last part before a variable
				const isLastBeforeVariable =
					index < parts.length - 1 && parts[index + 1].match(/\${([^}]+)}/);
				if (isLastBeforeVariable) {
					// Add a non-breaking space at the end of the text
					return React.createElement('span', { key: index }, part);
				}

				// For pure text that's not before a variable, allow wrapping
				return React.createElement(
					'span',
					{ key: index, className: 'break-words' },
					part
				);
			})
		);
	},

	activateOnboardingSteps: async (
		steps: OnboardingStep[],
		onboardingConfigId?: string
	) => {
		const userId = get().userId;
		const productId = get().productId;

		// Generate IDs for steps that don't have them
		const stepsWithIds = steps.map((step) => ({
			...step,
			id: step.id || uuidv4(),
		})) as OnboardingStep[];

		// Create onboarding log if needed
		if (userId && productId) {
			try {
				const data = await createOnboardingLog(
					userId,
					productId,
					onboardingConfigId
				);
				set({
					onboardingSteps: stepsWithIds,
					stepNum: 0,
					isOnboardingComplete: false,
					isOnboardingActive: true,
					onboardingLogId: data.log?.id ?? '',
				});
			} catch (error) {
				console.error('Error creating onboarding log:', error);
				// Still set the state even if log creation fails
				set({
					onboardingSteps: stepsWithIds,
					stepNum: 0,
					isOnboardingComplete: false,
					isOnboardingActive: true,
				});
			}
		} else {
			// Set state without creating log if no userId/productId
			set({
				onboardingSteps: stepsWithIds,
				stepNum: 0,
				isOnboardingComplete: false,
				isOnboardingActive: true,
				progressBar: {
					...get().progressBar,
				},
			});
		}
	},
});

// Helper: Determine if an email address belongs to a non-enterprise (free) provider
const isEnterpriseEmail = (email: string): boolean => {
	if (!email) return false;
	const domain = email.split('@')[1]?.toLowerCase();
	if (!domain) return false;

	// Extract the provider name (first segment before the first dot). Handle ccTLDs like gmail.co.uk
	const provider = domain.split('.')[0];

	const freeProviders = [
		'gmail',
		'yahoo',
		'hotmail',
		'outlook',
		'aol',
		'icloud',
		'protonmail',
		'gmx',
		'yandex',
		'live',
		'msn',
	];

	return !freeProviders.includes(provider);
};

// Helper: Fire Gumloop enrichment flow
const runUserEnrichment = async (
	email: string,
	companyDomain: string,
	productUserId: string,
	productId: string
) => {
	// Indicate that enrichment has started
	setCedarStore((state) => ({
		flags: { ...state.flags, userEnrichment: 'loading' },
	}));

	try {
		// Constants provided by user (no env vars required)
		const apiKey = 'fd6d612e3b734af5a713b0f3ae037bcb';
		const savedItemId = 'saR4iiSv6SVBJjXkFq7bmQ';
		const gumloopUserId = 'uDfJzs5lxjad07Ux8qMpBdZmZSv1';

		// 1. Trigger the Gumloop flow
		const triggerUrl = `https://api.gumloop.com/api/v1/start_pipeline?user_id=${gumloopUserId}&saved_item_id=${savedItemId}`;

		const roleEnrichmentUuid = uuidv4();
		const companyEnrichmentUuid = uuidv4();

		const triggerResponse = await fetch(triggerUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${apiKey}`,
			},
			body: JSON.stringify({
				email,
				company_domain: companyDomain,
				product_user_id: productUserId,
				product_id: productId,
				role_enrichment_uuid: roleEnrichmentUuid,
				company_enrichment_uuid: companyEnrichmentUuid,
			}),
		});

		if (!triggerResponse.ok) {
			console.error('Error triggering user enrichment flow:', triggerResponse);
			return;
		}

		const triggerJson: {
			run_id: string;
			saved_item_id: string;
			url: string;
			workbook_id: string;
		} = await triggerResponse.json();

		console.log('User enrichment flow triggered:', triggerJson);

		// 2. Poll Gumloop for the run results using the returned run_id
		const pollRunStatus = async (
			runId: string,
			maxAttempts = 15,
			intervalMs = 3000
		): Promise<unknown | void> => {
			const statusUrl = `https://api.gumloop.com/api/v1/get_pl_run?run_id=${runId}&user_id=${gumloopUserId}`;

			for (let attempt = 0; attempt < maxAttempts; attempt++) {
				try {
					const statusResponse = await fetch(statusUrl, {
						method: 'GET',
						headers: {
							Authorization: `Bearer ${apiKey}`,
						},
					});

					if (!statusResponse.ok) {
						console.error(
							'Error fetching enrichment run status:',
							statusResponse
						);
						return;
					}

					const statusJson: {
						state: string;
						outputs?: Record<string, unknown>;
						log?: unknown;
					} = await statusResponse.json();

					if (statusJson.state === 'DONE') {
						console.log('User enrichment flow completed:', statusJson.outputs);
						return statusJson.outputs;
					}

					if (
						statusJson.state === 'FAILED' ||
						statusJson.state === 'TERMINATED'
					) {
						console.error('User enrichment flow failed:', statusJson);
						return;
					}
				} catch (err) {
					console.error('Error polling enrichment flow status:', err);
					return;
				}

				// Wait before next attempt
				await new Promise((resolve) => setTimeout(resolve, intervalMs));
			}

			console.warn(
				'User enrichment flow did not complete within the expected time.'
			);
		};

		const enrichmentRaw = await pollRunStatus(triggerJson.run_id);
		// Parse expected structure: user_company & user_role arrays wrapping JSON strings
		let parsedResult: object | boolean = false;
		if (enrichmentRaw && typeof enrichmentRaw === 'object') {
			const raw = enrichmentRaw as {
				user_company?: string[];
				user_role?: string[];
			};
			const company =
				raw.user_company && raw.user_company[0]
					? JSON.parse(raw.user_company[0])
					: {};
			const role =
				raw.user_role && raw.user_role[0] ? JSON.parse(raw.user_role[0]) : {};
			parsedResult = { user_company: company, user_role: role };
		}
		// Store the parsed enrichment result (or false)
		setCedarStore((state) => ({
			flags: { ...state.flags, userEnrichment: parsedResult },
		}));
	} catch (error) {
		console.error('Error triggering user enrichment flow:', error);
		// Reset enrichment state on error (set to false)
		setCedarStore((state) => ({
			flags: { ...state.flags, userEnrichment: false },
		}));
	}
};
